import { Component, inject, OnInit, signal } from '@angular/core';
import { LayoutService, ModalService, SharedUiModule } from '@pf/shared-ui';
import { UserPositionFacade } from '@pf/platform-core/domain';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { map, switchMap } from 'rxjs';
import { Router } from '@angular/router';

@UntilDestroy()
@Component({
    selector: 'platform-active-position',
    template: `
        <pf-chip [pfColor]="activePosition().color">
            <span class="active-position-chip">
                <span class="active-position-chip__text">{{
                    activePosition().text
                }}</span>
                @if (hasActivePosition()) {
                    <pf-button
                        pfIcon="close"
                        error
                        buttonType="icon-rounded"
                        (click)="confirmExit()"
                        >Exit Position</pf-button
                    >
                }
            </span>
        </pf-chip>
    `,
    styles: [
        `
            :host {
                display: block;
                width: 100%;
                --pf-chip-width: 100%;

                pf-chip {
                    --pf-chip-width: 100%;
                    --pf-chip-height: 36px;
                    --pf-chip-border-radius: 20px;
                    --pf-chip-content-padding: 0;

                    ::ng-deep nz-tag {
                        display: flex;
                        align-items: center;
                        padding: 0 4px 0 0;

                        .chip__content {
                            width: 100%;

                            > span {
                                width: 100%;
                            }
                        }
                    }
                }

                .active-position-chip {
                    display: flex;
                    flex-direction: row;
                    justify-content: space-between;
                    align-items: center;
                    height: 100%;
                    width: 100%;

                    &__text {
                        flex: 1;
                        text-align: center;
                        font-weight: 500;
                        font-size: 0.8125rem;
                        padding: 0 12px;
                        overflow: hidden;
                        text-overflow: ellipsis;
                        white-space: nowrap;
                    }

                    pf-button {
                        flex-shrink: 0;
                        margin-left: auto;

                        ::ng-deep button {
                            height: 28px;
                            font-size: 0.75rem;
                            padding: 0 12px;
                        }
                    }
                }
            }
        `,
    ],
    standalone: true,
    imports: [SharedUiModule],
})
export class ActivePositionComponent implements OnInit {
    readonly positionsFacade = inject(UserPositionFacade);
    private readonly layoutService = inject(LayoutService);
    private readonly modalService = inject(ModalService);
    private readonly router = inject(Router);

    activePosition = signal({
        text: 'No Active Position',
        color: 'processing',
    });
    hasActivePosition = signal(false);

    ngOnInit(): void {
        this.positionsFacade.active$
            .pipe(
                untilDestroyed(this),
                map(position => {
                    if (!position?.userPositionTypeId) {
                        this.activePosition.set({
                            text: 'No Active Position',
                            color: 'processing',
                        });
                        this.hasActivePosition.set(false);
                    } else {
                        this.activePosition.set({
                            text: position.userPositionTypeName || 'None',
                            color: 'success',
                        });
                        this.hasActivePosition.set(true);
                    }
                })
            )
            .subscribe();
    }

    confirmExit() {
        this.modalService.confirmAction(
            'position',
            () => {
                this.positionsFacade
                    .exitPosition$()
                    .pipe(
                        switchMap(() => {
                            this.layoutService.closeMenu();
                            return this.router.navigateByUrl('/');
                        })
                    )
                    .subscribe();
            },
            'exit',
            () => {
                // do nothing on cancel
            },
            undefined,
            'Exiting will release any task and equipment you are currently assigned so it can be claimed by someone else.'
        );
    }
}
